// fetchers.js — page-data fetch helpers.
//
// Each function pulls fresh data from a /v1/* endpoint and updates
// the corresponding page-state object. All follow the same pattern:
//
//   1. Bail if already loading (loading-flag guard prevents
//      double-fetch when a quick re-render triggers before the
//      previous fetch resolves).
//   2. Set loading=true, try the API call, populate state, clear
//      error.
//   3. On failure, store the error message; on success, clear it.
//   4. Always set loading=false in `finally`.
//
// All imports: api from api.js (HTTP), state objects from
// page-state.js (live bindings — mutations propagate to consumers).

import { setRealConflictsSince } from "./format.js";
import { api, apiFetch } from "./api.js";
import { state } from "./state.js";
import {
  teamState,
  policyState,
  priorityState,
  spendState,
  decisionsState,
  coordinationState,
  collabDrillState,
  agentsState,
  worktreesState,
} from "./page-state.js";
import { collabAPIPath } from "./collab-drill.js";
import { decryptEventRow } from "./decrypt-helpers.js";
import { applyNavGating } from "./nav-gating.js";
import { isCloudMode } from "./ui.js";
import { withBuFilter } from "./bu-switcher.js";

// fetchTeamInfo → /v1/teams/info → teamState.info. Also kicks the
// nav-gating pass so the sidebar reflects the user's scope set on
// every info refresh (e.g., after a role change mid-session).
export async function fetchTeamInfo() {
  try {
    const info = await api("/v1/teams/info");
    if (info.error === "not_connected") {
      teamState.info = null;
    } else {
      teamState.info = info;
    }
  } catch (e) {
    // No team configured or coordinator unreachable — expected when
    // solo.
    teamState.info = null;
  }
  // nav-gating imports only ./page-state.js (no transitive cycle
  // back to fetchers), so a static import is safe.
  applyNavGating();
}

// fetchTeamMembers → /v1/teams/members → teamState.{members,people}
export async function fetchTeamMembers() {
  try {
    const data = await api("/v1/teams/members");
    if (data.error === "not_connected") return;
    teamState.members = data.members || [];
    teamState.people = data.people || [];
  } catch (e) {
    // No team configured — expected when solo.
  }
}

// fetchTeamData = fetchTeamInfo + fetchTeamMembers in parallel.
// Sets teamState.loading and lastFetch around the joint fetch so
// renderTeamPage's 5s cache TTL works.
export async function fetchTeamData() {
  if (teamState.loading) return;
  teamState.loading = true;
  await Promise.all([fetchTeamInfo(), fetchTeamMembers()]);
  teamState.loading = false;
  teamState.lastFetch = Date.now();
}

// fetchPolicy → /v1/rules → policyState.data
export async function fetchPolicy() {
  if (policyState.loading) return;
  policyState.loading = true;
  try {
    policyState.data = await api("/v1/rules");
    policyState.error = null;
  } catch (e) {
    policyState.error = e.message || "fetch failed";
  } finally {
    policyState.loading = false;
  }
}

// fetchPriority → /v1/priority → priorityState.data
export async function fetchPriority() {
  if (priorityState.loading) return;
  priorityState.loading = true;
  try {
    priorityState.data = await api("/v1/priority");
    priorityState.error = null;
  } catch (e) {
    priorityState.error = e.message || "fetch failed";
  } finally {
    priorityState.loading = false;
  }
}

// fetchSpend → /v1/spend?group=chat&since=<window> → spendState.dataByChat
// `window` is one of '24h' / '7d' / '30d' (selected by the page's
// window-picker dropdown; defaults to '30d'). The Spend page has a single
// view — the per-chat rollup — so this always fetches the grouped form.
// Server-side paginated: the table shows one page; the hero totals + pager
// use the window-wide total_rows the server returns.
export async function fetchSpend() {
  if (spendState.loading) return;
  spendState.loading = true;
  const pageSize = spendState.pageSize || 25;
  const offset = (spendState.page || 0) * pageSize;
  const paging = "&limit=" + pageSize + "&offset=" + offset;
  // Server-side search: ?q= matches agent_id/model across ALL rows so the
  // filtered total_rows + page agree (the table is server-paginated).
  const q = (spendState.filterText || "").trim();
  const search = q ? "&q=" + encodeURIComponent(q) : "";
  try {
    spendState.dataByChat = await api(
      "/v1/spend?group=chat&since=" +
        encodeURIComponent(spendState.window) +
        paging +
        search,
    );
    spendState.error = null;
  } catch (e) {
    spendState.error = e.message || "fetch failed";
    spendState.dataByChat = null;
  } finally {
    spendState.loading = false;
  }
}

// fetchValue → /v1/value → spendState.value. The triangulation that
// turns collisions prevented + edits coordinated + REAL spend into an
// estimated $ saved. Best-effort: on error it leaves value null so the
// band just doesn't render (never blocks the spend table). Uses the
// same window as the spend table (a Go duration like 24h/168h/720h).
export async function fetchValue() {
  try {
    // limit=50: ask for more than the server's default of 6 incident examples
    // so the Protection band's "Show all N" can reveal the fuller history
    // inline (the server clamps to 100). incidents_total still carries the true
    // count for the "Showing N of M" label.
    spendState.value = await api(
      "/v1/value?since=" + encodeURIComponent(spendState.window) + "&limit=50",
    );
    setRealConflictsSince(spendState.value && spendState.value.conflicts_unmanaged_since);
  } catch {
    spendState.value = null;
  }
}

// loadRealConflictsSince asks this computer when it started recording only
// real unheld changes, so every page shows those rows correctly.
export async function loadRealConflictsSince() {
  try {
    const v = await api("/v1/value?since=1h&limit=1");
    setRealConflictsSince(v && v.conflicts_unmanaged_since);
  } catch {
    // Unknown: old-tag rows keep showing as unheld changes.
  }
}

// fetchDecisions → decisionsState.list
//
// Endpoint depends on mode. The LOCAL daemon serves repo-local decision
// files at /v1/decisions. The HOSTED coordinator stubs /v1/decisions to
// `{ decisions: [], cloud_mode: true }` (decisions are repo-local files
// the coordinator never sees) — the real aggregated cloud data is the
// daemon-pushed rows at /v1/teams/decisions (resource_hint/body_hint,
// decrypted client-side by coordination.js). Pre-fix the hosted page
// always hit /v1/decisions and so always rendered empty even when chats
// were actively coordinating.
export async function fetchDecisions() {
  if (decisionsState.loading) return;
  decisionsState.loading = true;
  try {
    const url = isCloudMode()
      ? withBuFilter("/v1/teams/decisions")
      : "/v1/decisions";
    const data = await api(url);
    decisionsState.list = (data && data.decisions) || [];
    decisionsState.error = null;
  } catch (e) {
    decisionsState.error = e.message || "fetch failed";
  } finally {
    decisionsState.loading = false;
  }
}

// coordParams builds the shared ?since=&q=&limit=&offset= query for the
// cloud Coordination feed from coordinationState. `page` is the 0-based
// page for the source being fetched.
function coordParams(page) {
  const cs = coordinationState;
  const p = [];
  if (cs.window) p.push("since=" + encodeURIComponent(cs.window));
  if ((cs.q || "").trim()) p.push("q=" + encodeURIComponent(cs.q.trim()));
  p.push("limit=" + (cs.pageSize || 50));
  p.push("offset=" + (page || 0) * (cs.pageSize || 50));
  return p.join("&");
}

// fetchCoordinationDecisions → coordinationState.{decisions,decTotal}.
// Server-paginated + windowed + searched in BOTH modes (the local
// /v1/decisions endpoint gained since/q/limit/offset/total_rows to match
// the hosted getTeamDecisions, so the Coordination feed renders identically
// locally and on the hosted dashboard). HOSTED: encrypted resource_hint/
// body_hint + BU filter; LOCAL: plaintext resource/body, no BU filter.
export async function fetchCoordinationDecisions() {
  try {
    const params = coordParams(coordinationState.decPage);
    const url = isCloudMode()
      ? withBuFilter("/v1/teams/decisions?" + params)
      : "/v1/decisions?" + params;
    const data = await api(url);
    coordinationState.decisions = (data && data.decisions) || [];
    coordinationState.decTotal =
      data && typeof data.total_rows === "number"
        ? data.total_rows
        : coordinationState.decisions.length;
    coordinationState.error = null;
  } catch (e) {
    coordinationState.error = e.message || "fetch failed";
  } finally {
    coordinationState.decLoaded = true;
  }
}

// fetchCoordinationMessages → coordinationState.{messages,msgTotal}.
// HOSTED: /v1/teams/handoffs/threads (encrypted *_hint). LOCAL:
// /v1/a2a/threads (plaintext subject/last_body). Both now take
// since/q/limit/offset and return total_rows + a thread list, so the same
// renderer drives them. NOT BU-filtered (messages are agent-to-agent).
export async function fetchCoordinationMessages() {
  try {
    const params = coordParams(coordinationState.msgPage);
    const url = isCloudMode()
      ? "/v1/teams/handoffs/threads?" + params
      : "/v1/a2a/threads?" + params;
    const data = await api(url);
    coordinationState.messages =
      (data && (data.threads || data.handoffs)) || [];
    coordinationState.msgTotal =
      data && typeof data.total_rows === "number"
        ? data.total_rows
        : coordinationState.messages.length;
    coordinationState.error = null;
  } catch (e) {
    coordinationState.error = e.message || "fetch failed";
  } finally {
    coordinationState.msgLoaded = true;
  }
}

// fetchCollabItems → collabDrillState.{items,total,hasText} for the active
// drill. Team-server rows are events only; their resource is decrypted the
// way the Events page does it, so a reminder row can name its file.
export async function fetchCollabItems() {
  const cs = collabDrillState;
  const key = cs.drill.kind + "|" + cs.drill.days + "|" + cs.page;
  cs.loading = true;
  try {
    const data = await api(collabAPIPath(cs.drill, cs.page, cs.pageSize));
    let items = data.items;
    if (!data.has_text) {
      items = await Promise.all(
        items.map(async (it) => ({ ...it, resource: (await decryptEventRow(it)).resource })),
      );
    }
    cs.items = items;
    cs.hasText = data.has_text;
    cs.total = data.total_rows;
    cs.error = null;
  } catch (e) {
    cs.items = [];
    cs.total = 0;
    cs.error = e.message || "fetch failed";
  } finally {
    cs.loadedKey = key;
    cs.loading = false;
  }
}

// fetchAgents → /v1/agents → agentsState.list, plus the per-session detail
// the Sessions page shows: hook-reported working/idle state, held files,
// worktree branches and 30-day spend. The extras are best-effort — a daemon
// without one of those routes still lists sessions.
//
// A call while a fetch is in flight waits for that fetch. It used to return at
// once, so a render racing the first fetch drew "No sessions yet" over a list
// that was still loading.
let agentsInFlight = null;
export function fetchAgents() {
  if (!agentsInFlight) {
    agentsInFlight = loadAgents().finally(() => {
      agentsInFlight = null;
    });
  }
  return agentsInFlight;
}

// removeSession → DELETE /v1/agents/{id}. The daemon frees the files the
// session holds and hides it (its history stays). On success the list is
// fetched again so the row goes; on failure the message is kept on the row.
export async function removeSession(agentID) {
  agentsState.removing.add(agentID);
  delete agentsState.removeErrors[agentID];
  try {
    const resp = await apiFetch("/v1/agents/" + encodeURIComponent(agentID), { method: "DELETE" });
    if (!resp.ok) {
      let msg = "";
      try {
        const body = await resp.json();
        msg = body.message || body.error || "";
      } catch {
        // not JSON
      }
      throw new Error(msg || "HTTP " + resp.status);
    }
    // A fetch that started before the DELETE would bring the row back.
    if (agentsInFlight) await agentsInFlight;
    await fetchAgents();
    return true;
  } catch (e) {
    agentsState.removeErrors[agentID] = (e && e.message) || String(e);
    return false;
  } finally {
    agentsState.removing.delete(agentID);
  }
}

async function loadAgents() {
  agentsState.loading = true;
  // The hosted coordinator has no per-session detail routes; only the local
  // daemon is asked for them.
  const extra = (path) => (isCloudMode() ? Promise.resolve(null) : api(path).catch(() => null));
  try {
    const [data, states, leases, worktrees, spend] = await Promise.all([
      api("/v1/agents"),
      extra("/v1/sessions/state"),
      extra("/v1/leases?state=held"),
      extra("/v1/worktrees?active=true"),
      extra("/v1/spend?group=chat&since=30d&limit=200"),
    ]);
    agentsState.list = (data && data.agents) || [];
    agentsState.states = {};
    for (const st of (states && states.sessions) || []) {
      agentsState.states[st.agent_id] = st;
    }
    agentsState.leasesByAgent = {};
    for (const l of (leases && leases.leases) || []) {
      if (!agentsState.leasesByAgent[l.agent_id]) agentsState.leasesByAgent[l.agent_id] = [];
      agentsState.leasesByAgent[l.agent_id].push(l);
    }
    // A session's worktree: one AC allocated for it, or a discovered one
    // handed to it once it was seen working there. Newest wins.
    agentsState.worktreesByAgent = {};
    for (const w of (worktrees && worktrees.worktrees) || []) {
      const prev = agentsState.worktreesByAgent[w.agent_id];
      if (!prev || (w.created_at || "") > (prev.created_at || "")) agentsState.worktreesByAgent[w.agent_id] = w;
    }
    agentsState.spendByAgent = {};
    for (const r of (spend && spend.rows) || []) {
      agentsState.spendByAgent[r.agent_id] = r.cost_usd;
    }
    // The hosted page has no per-session routes, but the boot fetch already
    // holds the team's held leases and heartbeats, decrypted with the team
    // key: they give each session its files, task, repo and computer.
    agentsState.machineByAgent = {};
    if (isCloudMode()) {
      for (const l of state.leases || []) {
        if (!l.AgentID) continue;
        (agentsState.leasesByAgent[l.AgentID] ||= []).push({
          resource: l.Resource,
          purpose: l.Purpose,
          repo: l.Repo,
          acquired_at: l.AcquiredAt,
          last_heartbeat_at: l.LastHeartbeatAt,
        });
      }
      for (const h of state.heartbeats || []) {
        const label = h.deviceLabel || (state.deviceLabels && state.deviceLabels[h.laptop_id]) || "";
        if (h.agent_id && label) agentsState.machineByAgent[h.agent_id] = label;
      }
    }
    agentsState.fetchedAt = Date.now();
    agentsState.error = null;
  } catch (e) {
    agentsState.error = e.message || "fetch failed";
  } finally {
    agentsState.loading = false;
  }
}

// refreshWorktreesBadge → just the sidebar count, on the polling
// tick. Two paths because local + cloud have different endpoints:
//   - Local: hit /v1/worktrees?active=true, read active_count.
//   - Cloud: hit /v1/teams/worktrees?active=true&limit=1, read
//     active_count. We pass limit=1 because we only need the count
//     header — the row list is discarded and decryption is skipped.
// Bails on auth errors so the badge doesn't flicker for signed-out
// users; the page render path already handles the unauthorized UX.
//
// Why not just call fetchWorktrees(): in cloud mode fetchWorktrees
// doesn't exist on the right endpoint, and even if it did the full
// page render decrypts every row's path hints. This is the cheap
// "just the number" tick.
export async function refreshWorktreesBadge() {
  try {
    const url = isCloudMode()
      ? withBuFilter("/v1/teams/worktrees?active=true&limit=1")
      : "/v1/worktrees?active=true";
    const data = await api(url);
    const count = (data && data.active_count) || 0;
    const badge = document.getElementById("nav-worktrees-count");
    if (badge) badge.textContent = String(count);
    // Mirror into worktreesState so the page render can read a fresh
    // count if the user navigates over before its own fetch resolves.
    if (!isCloudMode()) {
      worktreesState.activeCount = count;
    }
  } catch (_e) {
    // Leave the previous badge value; next tick retries.
  }
}

// fetchWorktrees → /v1/worktrees (filtered by scope) →
// worktreesState.{list,activeCount,totalDiskMB}. Also updates the
// sidebar #nav-worktrees-count badge with the active-only count
// (even when viewing scope='all').
export async function fetchWorktrees() {
  if (worktreesState.loading) return;
  worktreesState.loading = true;
  try {
    const path =
      worktreesState.scope === "active"
        ? "/v1/worktrees?active=true"
        : "/v1/worktrees";
    const data = await api(path);
    worktreesState.list = (data && data.worktrees) || [];
    worktreesState.activeCount = (data && data.active_count) || 0;
    worktreesState.totalDiskMB = (data && data.total_disk_mb) || 0;
    worktreesState.error = null;
    // Update the sidebar badge with the active-only count even if
    // the user is currently viewing 'all'.
    //
    // When we're on scope='all' we also know the total, so show
    // "active/total" — a bare "1" beside a list of hundreds of worktrees
    // reads as a broken counter (this machine has ~1,890 rows and 1 active
    // one; 2026-08-28). On scope='active' the list IS the active set, so
    // there's no second number to show and it stays a single count rather
    // than printing a total we'd be guessing at.
    const badge = document.getElementById("nav-worktrees-count");
    if (badge) {
      const active = worktreesState.activeCount;
      const knowTotal = worktreesState.scope !== "active";
      const total = worktreesState.list.length;
      const showTotal = knowTotal && total > active;
      badge.textContent = showTotal ? active + "/" + total : String(active);
      badge.title = showTotal
        ? active + " active now · " + total + " total worktrees"
        : active + " active now";
    }
  } catch (e) {
    worktreesState.error = e.message || "fetch failed";
  } finally {
    worktreesState.loading = false;
  }
}
